import { fetcher, useCollectionQuery, useMutationFn } from '@rocketlane/state-db';
import { // useQueryClient is retained ONLY for cross-entity (project-detail and
// project-dependency-map) cache invalidation below — those are NOT task data,
// which now lives reactively in `taskCollection`.
useQueryClient } from '@tanstack/react-query';
import { insertPlanEntityTokenOptimistic, refreshPlanLanesForPhase, refreshProjectPlanLanes } from 'collections/plan-board';
import { fetchProjectDetail, getProjectFromCollection, projectCollection } from 'collections/project';
import { createTaskRecord, fetchGanttLightTaskList, fetchTask, fetchTaskAdditionalFields, fetchTaskDefaultFields, fetchTaskList, getTaskAdditionalFieldsCollectionQueryKey, getTaskDefaultFieldsCollectionQueryKey, getTaskDetailCollectionQueryKey, hasGanttLightTaskListLoaded, taskCollection, updatePhaseRecord } from 'collections/tasks';
import { ApiStatusCode } from 'api/axios';
import { useCallback, useState } from 'react';
import { logger } from 'services';
import { StatusGroup, TaskCardPosition } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { converToEntityId, PhaseEntityType } from 'components/organisms/projects/project-plan/phase-entity';
import { useStatusFieldV2 } from 'data/fields/hooks';
import { useTaskPaneUpdateMiniEnabled } from 'hooks/use-task-pane-update-mini';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';
import { queueMacrotask } from 'utils/macrotask';
import { negativeUid } from 'utils/uid';
import { getProjectDependencyMapKey } from './common';
import { tenMinutes } from './constants';
const isPolicyConflictError = (error)=>{
    var _error_response;
    return (error === null || error === void 0 ? void 0 : (_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === ApiStatusCode.PolicyConflict;
};
export const useProjectTasksQuery = (projectId, options = {}, config)=>{
    var _options_enabled;
    // Single collection-backed read: the fetch lands tasks in `taskCollection`,
    // and `select` derives this project's tasks reactively from the collection.
    // No React Query / manual live-query overlay needed. Only `enabled` is
    // honored from the RQ-shaped options — consumers use it to gate the
    // full-list fetch off (e.g. the paginated plan board supplies per-lane
    // pages, so mounting it must not trigger the whole project's task list).
    const result = useCollectionQuery({
        collection: taskCollection,
        queryKey: `tasks:list:${projectId[1]}`,
        queryFn: ()=>fetchTaskList(projectId[1], config),
        // O(result) via the `byProject` index — not an O(n) `getAll()` scan. This
        // select re-runs on every task edit (it's the collection subscriber), so an
        // O(n) scan here was a per-edit full-collection sweep.
        select: (c)=>c.getByIndex('byProject', projectId[1]),
        enabled: ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true) && !!projectId[1] && projectId[1] > 0,
        staleMs: tenMinutes
    });
    return result;
};
/**
 * Light task list for the gantt timeline (heavy fields hydrate per row).
 * Distinct queryKey so it never dedupes against the full-list fetch.
 */ const ganttLightTasksQueryKey = (projectId)=>`tasks:gantt-light:${projectId[1]}`;
/**
 * Light list loaded, or its fetch in flight. STICKY: pickers over the
 * timeline must keep reading the light query, or a stale picker would
 * re-fire the gateway-timeout full fetch.
 */ export const isGanttLightTaskListActive = (projectId)=>hasGanttLightTaskListLoaded(projectId[1]) || fetcher.isFetching(ganttLightTasksQueryKey(projectId));
export const useProjectGanttTasksQuery = (projectId, options = {})=>{
    var _options_enabled;
    return useCollectionQuery({
        collection: taskCollection,
        queryKey: ganttLightTasksQueryKey(projectId),
        queryFn: ()=>fetchGanttLightTaskList(projectId[1]),
        select: (c)=>c.getByIndex('byProject', projectId[1]),
        enabled: ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true) && !!projectId[1] && projectId[1] > 0,
        staleMs: tenMinutes
    });
};
/**
 * Tasks of a single PHASE, derived reactively from the collection.
 *
 * Shares the project-list fetch (same `queryKey`, deduped by the Fetcher — one
 * network call no matter how many phases render) but selects via the `byPhase`
 * index, so each swimlane subscribes to ONLY its phase: an edit to a task in
 * another phase leaves this result reference-equal and does NOT re-render this
 * swimlane. Replaces passing a phase-filtering `select` to `useProjectTasksQuery`
 * (which the collection-backed hook ignored — every swimlane was subscribing to
 * the whole project and re-rendering on every task edit).
 */ export const useProjectPhaseTasksQuery = (projectId, phaseId, options = {}, config)=>{
    var _options_enabled;
    return useCollectionQuery({
        collection: taskCollection,
        queryKey: `tasks:list:${projectId[1]}`,
        queryFn: ()=>fetchTaskList(projectId[1], config),
        select: (c)=>phaseId == null ? [] : c.getByIndex('byPhase', phaseId),
        // `enabled: false` gates the full-list fetch AND the subscription off —
        // paginated plan lanes read per-lane entity pages instead.
        enabled: ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true) && !!projectId[1] && projectId[1] > 0,
        staleMs: tenMinutes
    });
};
/**
 * Subtasks of one parent task, derived reactively from the collection.
 *
 * Populated by the PARENT's default-fields fetch — whose payload embeds the
 * subtasks, which `fetchTaskDefaultFields` explodes into the collection. Uses
 * the same fetcher key as the drawer's own default-fields query
 * (`useTaskDetailsDefaultFieldsQuery`), so the two dedupe to a SINGLE network
 * call. Previously this shared the full-project `fetchTaskList` just to hydrate
 * a handful of subtask rows — an O(project) fetch (and a full-list stale-write
 * on the paginated board) to render one parent's children.
 *
 * Selects via the `byParent` index — so this list re-renders ONLY when a
 * subtask of THIS parent changes. A newly-created subtask is pushed with
 * `parentTask` set and appears immediately, without the parent's
 * `subTaskOrder` having to refresh.
 */ export const useSubtasksQuery = (projectId, parentTaskId)=>useCollectionQuery({
        collection: taskCollection,
        queryKey: parentTaskId == null ? 'tasks:default-fields:none' : getTaskDefaultFieldsCollectionQueryKey(projectId[1], parentTaskId),
        queryFn: ()=>fetchTaskDefaultFields({
                projectId: projectId[1],
                taskId: parentTaskId !== null && parentTaskId !== void 0 ? parentTaskId : 0
            }),
        select: (c)=>parentTaskId == null ? [] : c.getByIndex('byParent', parentTaskId),
        enabled: !!projectId[1] && projectId[1] > 0 && parentTaskId != null,
        staleMs: tenMinutes
    });
/**
 * Optimistically create subtasks under `parentTaskId`. Mirrors
 * `useCreateTaskOptimisticMutation`: each subtask is pushed into the collection
 * immediately with a NEGATIVE temp id (`negativeUid`) and a Todo status, so it
 * appears in the subtask list right away (via the `byParent` index). A negative
 * id marks the row as "creating" → rendered disabled (see `TaskTable`) until the
 * server confirms. The real bulk-create runs on a macrotask; on success the
 * server rows (real ids) are pushed by `addMultipleSubtasks` and the temps are
 * removed; on failure the temps are removed and an error toast is shown.
 */ export const useCreateSubtasksOptimisticMutation = (projectId, parentTaskId)=>{
    const taskStatusField = useStatusFieldV2();
    const queryClient = useQueryClient();
    const createSubtasks = (subtaskNames)=>{
        const temps = subtaskNames.map((taskName)=>({
                taskId: negativeUid(),
                taskName,
                parentTask: parentTaskId,
                project: {
                    projectId
                },
                fields: [
                    {
                        ...taskStatusField,
                        fieldValue: StatusGroup.Todo
                    }
                ]
            }));
        taskCollection.push(temps);
        queueMacrotask(()=>{
            // Use the proven single-create endpoint (`createTaskRecord` →
            // `TaskAPI.createTask` with `parentTask`) — the same call the single
            // subtask saga uses — once per subtask. Each pushes the real row (real
            // id) into the collection; we then drop the matching temp so the list
            // swaps cleanly.
            Promise.all(subtaskNames.map((taskName)=>createTaskRecord(projectId, {
                    taskName,
                    parentTask: parentTaskId
                }))).then(()=>{
                temps.forEach((t)=>taskCollection.externalDelete(t.taskId));
                queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));
                invalidateTaskQuery(parentTaskId);
            }).catch((error)=>{
                temps.forEach((t)=>taskCollection.externalDelete(t.taskId));
                logger.error('createSubtasks failed', {
                    error
                });
                // 422 is handled globally via the policy conflict drawer — don't
                // also surface a generic failure toast.
                if (!isPolicyConflictError(error)) {
                    toastService({
                        title: 'Failed to create subtask',
                        message: ''
                    }).error();
                }
            });
        });
        return Promise.resolve();
    };
    return {
        createSubtasks
    };
};
export const useUpdatePhaseTaskMutation = (taskId)=>{
    // useQueryClient is kept ONLY for cross-entity cache invalidation
    // (project-detail + project-dependency-map). Task data itself is now
    // reactive via `taskCollection` (updatePhaseRecord pushes the result), so the
    // previous setQueryData(task-detail) + refetchQueries(project-tasks) are gone.
    const queryClient = useQueryClient();
    return useMutationFn({
        mutationFn: (payload)=>updatePhaseRecord(taskId, payload),
        onSuccess: async (task)=>{
            const { project: { projectId } } = task;
            // Detail is collection-backed — refetch+push (phase change affects the
            // project's phase counts).
            await fetchProjectDetail(projectId);
            await queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));
        }
    });
};
export const useTaskDetailQuery = (taskId, queryOptions = {})=>{
    // Collection-backed detail read: fetchTask pushes the task into the
    // collection, `select` reads it back reactively.
    const result = useCollectionQuery({
        collection: taskCollection,
        queryKey: getTaskDetailCollectionQueryKey(taskId[2]),
        queryFn: ()=>fetchTask(taskId[1], taskId[2]),
        select: (c)=>c.get(taskId[2]),
        enabled: queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.enabled
    });
    return result;
};
export const getTaskDetailFieldsQueryKey = ({ queryString, projectId, taskId })=>[
        queryString,
        projectId,
        taskId
    ];
export const useTaskDetailsDefaultFieldsQuery = ({ projectId, taskId }, queryOptions = {})=>{
    const result = useCollectionQuery({
        collection: taskCollection,
        queryKey: getTaskDefaultFieldsCollectionQueryKey(projectId, taskId),
        queryFn: ()=>fetchTaskDefaultFields({
                projectId,
                taskId
            }),
        select: (c)=>c.get(taskId),
        enabled: queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.enabled
    });
    return result;
};
export const useTaskDetailsAdditionalFieldsQuery = ({ projectId, taskId }, queryOptions = {})=>{
    const result = useCollectionQuery({
        collection: taskCollection,
        queryKey: getTaskAdditionalFieldsCollectionQueryKey(projectId, taskId),
        queryFn: ()=>fetchTaskAdditionalFields({
                projectId,
                taskId
            }),
        select: (c)=>c.get(taskId),
        enabled: queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.enabled
    });
    return result;
};
export const useCreateTaskOptimisticMutation = (projectId, phaseId)=>{
    const taskStatusField = useStatusFieldV2();
    const [tasksInCreation, setTasksInCreation] = useState([]);
    // Maps an optimistic temp task id (negative) → the server-assigned real id
    // once the create persists. The swimlane uses this to hide the temp
    // `TaskCardWrapper` the instant the real card enters the paginated lane
    // order, so the optimistic card and the persisted card never render together
    // (a transient overlap trips Playwright strict mode — see TC-1107).
    const [createdRealIdByTempId, setCreatedRealIdByTempId] = useState({});
    const createTask = (payload)=>{
        const tempTaskId = negativeUid();
        const taskCardPosition = payload.taskCardPosition === TaskCardPosition.Top ? 0 : null;
        setTasksInCreation((prev)=>{
            const newTask = {
                ...payload,
                taskId: tempTaskId,
                fields: [
                    {
                        ...taskStatusField,
                        fieldValue: StatusGroup.Todo
                    }
                ],
                project: {
                    projectId: projectId[1]
                }
            };
            taskCollection.push([
                newTask
            ]);
            if (taskCardPosition === 0) {
                return [
                    newTask,
                    ...prev
                ];
            }
            return [
                ...prev,
                newTask
            ];
        });
        queueMacrotask(()=>{
            payload = {
                ...payload,
                position: taskCardPosition
            };
            createTaskRecord(projectId[1], payload).then((newTask)=>{
                var _ref;
                var _newTask_projectPhase;
                // Remember the temp→real id mapping so the swimlane can drop the
                // optimistic card the moment the real card lands in the lane order.
                setCreatedRealIdByTempId((m)=>({
                        ...m,
                        [tempTaskId]: newTask.taskId
                    }));
                // Task data lands reactively via createTaskRecord →
                // taskCollection.push; no project-tasks cache manipulation needed.
                // queryClient is retained ONLY for the cross-entity project-detail
                // cache (phase counts / entityOrder) below.
                // Detail is collection-backed — read the project from the collection
                // and optimistically bump the phase task counts/entityOrder by
                // pushing the patched project back (replaces getQueryData/setQueryData
                // on the now-dead detail key). Guarded: skip if the project isn't
                // hydrated yet (principle 1 — don't deref a possibly-undefined).
                const previousProjectData = getProjectFromCollection(projectId[1]);
                if (previousProjectData) {
                    const optimisticProjectData = {
                        ...previousProjectData,
                        projectPhases: previousProjectData.projectPhases.map((phase)=>{
                            if (phase.projectPhaseId === phaseId) {
                                var _ref, _phase_totalTaskCount;
                                var _phase_aggregateProgressValues;
                                return {
                                    ...phase,
                                    aggregateProgressValues: {
                                        ...phase.aggregateProgressValues,
                                        taskCount: ((_ref = phase === null || phase === void 0 ? void 0 : (_phase_aggregateProgressValues = phase.aggregateProgressValues) === null || _phase_aggregateProgressValues === void 0 ? void 0 : _phase_aggregateProgressValues.taskCount) !== null && _ref !== void 0 ? _ref : 0) + 1
                                    },
                                    totalTaskCount: ((_phase_totalTaskCount = phase.totalTaskCount) !== null && _phase_totalTaskCount !== void 0 ? _phase_totalTaskCount : 0) + 1,
                                    entityOrder: phase.entityOrder ? taskCardPosition === 0 ? [
                                        converToEntityId(newTask.taskId, PhaseEntityType.Task),
                                        ...phase.entityOrder
                                    ] : [
                                        ...phase.entityOrder,
                                        converToEntityId(newTask.taskId, PhaseEntityType.Task)
                                    ] : [
                                        converToEntityId(newTask.taskId, PhaseEntityType.Task)
                                    ]
                                };
                            }
                            return phase;
                        })
                    };
                    projectCollection.push([
                        optimisticProjectData
                    ]);
                }
                const startDate = newTask.startDate || newTask.dueDate || null;
                const dueDate = newTask.dueDate || newTask.startDate || null;
                // A create touches exactly ONE phase — refresh only that phase's
                // lane, not every loaded lane on the board. refreshProjectPlanLanes
                // refetched all loaded lanes (a whole-board sweep => one batch call
                // per ~12 lanes) for a single-phase insert.
                const createdPhaseId = (_ref = (_newTask_projectPhase = newTask.projectPhase) === null || _newTask_projectPhase === void 0 ? void 0 : _newTask_projectPhase.projectPhaseId) !== null && _ref !== void 0 ? _ref : phaseId;
                if (createdPhaseId != null) {
                    // Put the real token into the lane NOW so the card is continuous:
                    // lanes render from pageTokens, not the taskCollection push above,
                    // so without this the card vanishes until the (slow) batch refetch
                    // returns fresh tokens. The scoped refetch below reconciles order.
                    insertPlanEntityTokenOptimistic(projectId[1], createdPhaseId, converToEntityId(newTask.taskId, PhaseEntityType.Task), taskCardPosition === 0 ? 'top' : 'bottom');
                    refreshPlanLanesForPhase(projectId[1], createdPhaseId);
                } else {
                    // No known phase (not a plan-board create) — whole-board fallback.
                    refreshProjectPlanLanes(projectId[1]);
                }
                if (startDate != null && dueDate != null) {
                    taskCollection.update(newTask.taskId, {
                        startDate,
                        dueDate
                    });
                }
            }).catch((e)=>{
                logger.error('Error creating task', {
                    error: e
                });
                // 422 is handled globally via the policy conflict drawer — don't
                // also surface a generic failure toast.
                if (!isPolicyConflictError(e)) {
                    toastService({
                        title: 'Failed to create task',
                        message: ''
                    }).error();
                }
            }).finally(()=>{
                // Drop the optimistic temp from the collection so collection-
                // reading surfaces never keep a ghost (or temp+real pair).
                taskCollection.externalDelete(tempTaskId);
                setTasksInCreation((prev)=>prev.filter((t)=>t.taskId !== tempTaskId));
                setCreatedRealIdByTempId((m)=>{
                    if (!(tempTaskId in m)) {
                        return m;
                    }
                    const next = {
                        ...m
                    };
                    delete next[tempTaskId];
                    return next;
                });
            });
        });
    };
    return {
        tasksInCreation,
        createTask,
        createdRealIdByTempId
    };
};
// NOTE: The task-list RQ cache is dead (collection-backed). `onFetchTaskSuccess`
// previously wrote the updated task into getProjectTasksQueryKey via
// setQueryData — nothing reads that key anymore (the collection is the source of
// truth and updates reactively), so the write is a no-op and has been removed.
// The hook + signature are retained because consumers still call
// `onFetchTaskSuccess` as their fetch-success callback.
export const useTaskUpdate = (_taskUpdate)=>{
    const onFetchTaskSuccess = useCallback((_newTask)=>{
    // No-op: task data lands reactively in taskCollection; no RQ write needed.
    }, []);
    return {
        onFetchTaskSuccess
    };
};
export const useTaskPaneUpdate = (taskId)=>{
    const [, projectId] = taskId;
    const { onFetchTaskSuccess } = useTaskUpdate([
        projectId
    ]);
    const skipRefetch = useTaskPaneUpdateMiniEnabled() || window.location.pathname.includes(`/projects/${projectId}/plan`);
    // NOTE: Task-detail RQ caches are dead (collection-backed). This previously
    // wrote the updated task into the task-detail / task-detail-fields query keys
    // via setQueryData — nothing reads those keys anymore, so the writes were
    // no-ops and have been removed. Signature retained for consumers.
    const onTaskUpdateSuccess = useCallback(async (_newTask)=>{
    // No-op: task data lands reactively in taskCollection.
    }, []);
    // The WS push already updates taskCollection reactively, so the previous
    // refetchQueries(task-detail) was redundant and is removed. The handler shape
    // is kept (consumers register it as a socket-update callback).
    const handleTaskSocketUpdate = useCallback(async (_socketData)=>{
    // No-op: collection is updated by the WS push; no RQ refetch needed.
    }, []);
    return {
        onFetchTaskSuccess,
        handleTaskSocketUpdate,
        onTaskUpdateSuccess,
        skipRefetch
    };
};
